import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { ArrowRight, HelpCircle, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import PracticeModelInvestment from "@/components/PracticeModelInvestment";

const FAQ_CATEGORIES = [
  {
    category: "About Ketamine-Assisted Therapy (KAP)",
    faqs: [
      {
        q: "What is Ketamine-Assisted Psychotherapy (KAP)?",
        a: "Ketamine-Assisted Psychotherapy (KAP) is a treatment that pairs low, therapeutic doses of ketamine with structured psychotherapy sessions. Ketamine is a legal psychedelic that works with the NMDA receptors in the brain, enhancing neuroplasticity so you can form new neural pathways and process trauma in ways talk therapy alone often cannot reach.",
      },
      {
        q: "How does ketamine actually work for depression, anxiety, and trauma?",
        a: "Ketamine temporarily quiets the default mode network and enhances neuroplasticity — your brain's ability to build new connections. This creates a therapeutic window where old patterns feel less rigid and new perspectives can take hold. When paired with skilled psychotherapy (KAP), those insights get integrated into lasting change rather than fading.",
      },
      {
        q: "Is ketamine legal for therapeutic use?",
        a: "Yes. Ketamine is FDA-approved and legally prescribed off-label for mental health treatment. Laura uses the tablet form for her telemedicine sessions, prescribed by a licensed collaborating psychiatric provider.",
      },
      {
        q: "What conditions can KAP help with?",
        a: "KAP has been shown to help with depression, treatment-resistant depression, anxiety, PTSD, complex trauma, hyper-independence and attachment wounds, high-functioning burnout, and existential distress. Laura specializes in hyper-independent women whose talk therapy has plateaued.",
      },
    ],
  },
  {
    category: "Sessions & What to Expect",
    faqs: [
      {
        q: "What does a ketamine therapy session feel like?",
        a: "Most clients describe it as a soft, floating dissociation from the usual mental chatter. You'll be lying in a comfortable spot at home with an eye mask, headphones, and curated music. Laura is with you on a secure video connection the entire time. The experience typically lasts 45–60 minutes, followed by a return to baseline.",
      },
      {
        q: "Where do sessions take place?",
        a: "100% online via secure HIPAA-compliant video. You'll be in your own home, in a quiet space you've prepared. No commute, no waiting room, no performing wellness on the way home.",
      },
      {
        q: "How long does a full KAP journey take?",
        a: "It depends on the intensive. The 4-Month DC Intensive spans 16 weeks and 24 clinical hours. The 9-Week Group Immersion spans 9 weeks with 14.5 clinical hours. Continuation care is available for anyone who wants to go deeper afterward.",
      },
      {
        q: "How quickly will I feel results?",
        a: "Many clients experience noticeable shifts within hours to days of the first dosing session — much faster than SSRIs, which typically take weeks. The full transformation usually builds across the intensive as new neural pathways solidify and integration work deepens.",
      },
      {
        q: "Is ketamine therapy safe?",
        a: "When done under licensed clinical supervision at therapeutic doses, KAP has a strong safety profile. You'll complete a psychiatric diagnostic evaluation before starting, and Laura or her collaborating provider will screen for contraindications (uncontrolled blood pressure, active psychosis, pregnancy, etc.).",
      },
      {
        q: "Are there side effects?",
        a: "Common short-term effects include mild nausea, dizziness, temporary blood pressure elevation, and vivid dissociation during the session. These resolve within an hour or two. Serious side effects are rare with proper screening and clinical support.",
      },
    ],
  },
  {
    category: "Cost, Payment & Insurance",
    faqs: [
      {
        q: "How much does ketamine-assisted therapy cost?",
        a: "Laura offers three primary intensives: the 9-Week Group Immersion ($2,500 for FL/ME residents), the DC 4-Month Individual Intensive ($6,000), and three individual FL/ME intensives ranging from Foundation to Continuation. Individual sessions are $350/hour when purchased outside an intensive. Intensive clients save $100 per session hour.",
      },
      {
        q: "Does insurance cover ketamine therapy?",
        a: "Laura is out-of-network. Many PPO plans reimburse 40–80% of out-of-network mental health costs after your deductible. Laura provides superbills for you to submit for reimbursement. Call your insurance and ask about \"out-of-network mental health benefits\" and \"CPT code 90837\".",
      },
      {
        q: "Are payment plans available?",
        a: "Yes. Payment plans are available for all intensive tiers. Book a free 40-minute consultation and we'll walk through options that work for your budget.",
      },
      {
        q: "Why don't you accept insurance directly?",
        a: "In-network reimbursement rates severely limit how much time a therapist can spend with each client. Staying out-of-network lets Laura maintain the depth, container, and integration support that KAP actually requires. Superbills for reimbursement are provided at no extra cost.",
      },
    ],
  },
  {
    category: "Who KAP Is (and Isn't) For",
    faqs: [
      {
        q: "Who is Laura's ideal client?",
        a: "Hyper-independent women who are tired of being the strong one, feel unsafe in relationships, have tried talk therapy for years without lasting change, and are ready for spiritually integrated, trauma-informed KAP. Most clients are high-functioning professionals, mothers, or leaders who've been carrying everyone else.",
      },
      {
        q: "Do I need to have tried other therapies first?",
        a: "No, but many clients come to KAP after years of talk therapy plateaued. If you've done the work of self-awareness and still feel stuck, KAP is often the doorway to the shift you've been searching for.",
      },
      {
        q: "Where do I need to live to work with Laura?",
        a: "Laura is licensed in Florida, Maine, and Washington DC. You must legally reside in one of those states during your sessions.",
      },
      {
        q: "Can men or couples work with Laura?",
        a: "Laura's practice specifically serves hyper-independent women. If you're a man or a couple seeking KAP, she can offer referrals to trusted colleagues.",
      },
    ],
  },
  {
    category: "Getting Started",
    faqs: [
      {
        q: "How do I book a free consultation?",
        a: "Click any \"Book Free Consultation\" button on the site, or head to the Book page. The 40-minute call is complimentary — no pressure, just an honest conversation to see if KAP with Laura is the right next step for you.",
      },
      {
        q: "What happens after the free consultation?",
        a: "If it feels like a fit for both of you, Laura will schedule your Psychiatric Diagnostic Evaluation (included in every intensive) and walk you through the treatment planning process. Your first dosing session usually happens within 2–3 weeks of enrollment.",
      },
    ],
  },
];

const ALL_FAQS = FAQ_CATEGORIES.flatMap((cat) => cat.faqs);

const FAQPage = () => {
  const seoOverride = usePageSeo("faq-page");
  const description =
    seoOverride?.meta_description ||
    "Answers to the most common questions about Ketamine-Assisted Psychotherapy (KAP): how it works, cost, insurance, side effects, and what to expect. From Laura A. Jeanis, LCSW — licensed in FL, ME & DC.";

  // FAQPage JSON-LD schema for Google People Also Ask
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: ALL_FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: {
        "@type": "Answer",
        text: f.a,
      },
    })),
  };

  return (
    <div className="pt-20" data-testid="faq-page">
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title="Ketamine-Assisted Therapy (KAP) FAQ — Cost, Safety, Side Effects & More"
        description={description}
        pathname="/faq"
      />
      <Helmet
        script={[
          {
            type: "application/ld+json",
            innerHTML: JSON.stringify(faqSchema),
          },
        ]}
      />

      {/* Hero */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium">
              <HelpCircle className="h-4 w-4" />
              Frequently Asked Questions
            </span>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight mt-6">
              Everything You Want To Know About{" "}
              <span className="italic text-primary">Ketamine-Assisted Therapy</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Straight answers on how KAP works, what sessions feel like, cost,
              insurance, safety, and who this treatment is for. Written by Laura A.
              Jeanis, LCSW.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Sections */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto space-y-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="flex flex-col sm:flex-row items-center gap-6 rounded-3xl border border-primary/15 bg-secondary/30 p-6 md:p-8 text-center sm:text-left"
            data-testid="faq-author-card"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/l4ja8zkq_IMG_4558_Facetune_17-01-2023-21-22-22.jpeg"
              alt="Laura A. Jeanis, LCSW, LICSW — licensed ketamine psychotherapist answering KAP questions"
              className="h-28 w-28 md:h-32 md:w-32 rounded-full object-cover object-top ring-4 ring-white shadow-soft flex-shrink-0"
              loading="lazy"
              data-testid="faq-laura-photo"
            />
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-primary font-medium">Answers written by</p>
              <p className="mt-2 font-serif text-2xl font-semibold text-foreground leading-tight">Laura A. Jeanis</p>
              <p className="text-sm text-muted-foreground mt-1">LCSW, LICSW, CCTP, CEO · Licensed in FL, ME &amp; DC</p>
            </div>
          </motion.div>
          {FAQ_CATEGORIES.map((category, catIndex) => (
            <motion.div
              key={category.category}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: catIndex * 0.05 }}
              data-testid={`faq-category-${catIndex}`}
            >
              <div className="mb-6">
                <span className="inline-flex items-center gap-2 text-primary font-medium text-xs uppercase tracking-[0.18em]">
                  <Sparkles className="h-4 w-4" />
                  Section {catIndex + 1}
                </span>
                <h2 className="font-serif text-2xl sm:text-3xl font-semibold text-foreground mt-3">
                  {category.category}
                </h2>
              </div>

              <Accordion type="single" collapsible className="space-y-3">
                {category.faqs.map((faq, i) => (
                  <AccordionItem
                    key={faq.q}
                    value={`item-${catIndex}-${i}`}
                    className="border border-border/40 rounded-2xl px-5 bg-white hover:border-primary/30 transition-colors"
                    data-testid="faq-item"
                  >
                    <AccordionTrigger className="text-left font-serif text-lg font-semibold text-foreground hover:no-underline py-5">
                      {faq.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground leading-relaxed pb-5 text-base">
                      {faq.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Practice Model & Investment */}
      <PracticeModelInvestment background="bg-secondary/30" />

      {/* Closing CTA */}
      <section className="py-20 md:py-24 px-6 md:px-12 bg-white">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-3xl md:text-4xl font-semibold text-foreground leading-tight">
              Still have questions? Let&rsquo;s talk.
            </h2>
            <p className="mt-5 text-lg text-muted-foreground leading-relaxed">
              Book a free 40-minute consultation with Laura. No pressure — just an
              honest conversation about whether KAP is the right next step for you.
            </p>
            <div className="mt-10 flex flex-wrap justify-center gap-4">
              <Link to="/book">
                <Button
                  className="rounded-full px-8 py-6 text-base bg-primary text-primary-foreground hover:bg-primary/90"
                  data-testid="faq-book-cta"
                >
                  Book Your Free Consultation
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="rounded-full px-8 py-6 text-base border-primary/30 text-primary hover:bg-primary/5"
                >
                  Ask Laura a Question
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
